import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Avatar,
  Badge,
  Paper,
  ActionIcon,
  Button,
  Image,
  Grid,
  Tabs,
  Divider,
  Menu,
  TextInput,
  LoadingOverlay,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { env } from '../../utils/env';
import { AsyncImage } from '../../utils/imageUtils';
import editIcon from '../../assets/icons/edit.svg';
import trashIcon from '../../assets/icons/trash.svg';
import commentIcon from '../../assets/icons/comment.svg';
import clockIcon from '../../assets/icons/clock.svg';
import categoryIcon from '../../assets/icons/tag.svg';
import sendIcon from '../../assets/icons/send.svg';
import dotsIcon from '../../assets/icons/dots.svg';
import { useDropzone } from 'react-dropzone';
import uploadIcon from '../../assets/icons/upload.svg';
import { Expense, ExpenseSplit } from '../../hooks/useExpenseDetails';

interface ExpenseDetailProps {
  expenseId: string;
  groupId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (expense: any) => void;
  onDelete: (expenseId: string) => Promise<void>;
  accessToken: string;
  current_user?: any;
}

export const ExpenseDetail = ({
  expenseId,
  groupId,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  accessToken,
  current_user,
}: ExpenseDetailProps) => {
  const [activeTab, setActiveTab] = useState<string | null>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expense, setExpense] = useState<Expense | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const commentForm = useForm({
    initialValues: {
      text: '',
    },
    validate: {
      text: (value) => (!value.trim() ? 'Comment cannot be empty' : null),
    },
  });

  // Fetch expense details and comments
  React.useEffect(() => {
    const fetchExpenseDetails = async () => {
      if (!isOpen) return;

      try {
        setLoading(true);
        console.log("Current user data:", current_user);

        const [expenseResponse, commentsResponse] = await Promise.all([
          fetch(`${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }),
          fetch(`${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}/comments`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }),
        ]);

        if (!expenseResponse.ok || !commentsResponse.ok) {
          throw new Error('Failed to fetch expense details');
        }

        const [expenseData, commentsData] = await Promise.all([
          expenseResponse.json(),
          commentsResponse.json(),
        ]);

        console.log("Comments data:", commentsData);

        setExpense(expenseData);
        // Process comments to add user information
        const processedComments = commentsData.map((comment: any) => {
          // Convert IDs to strings for safer comparison (in case one is string and one is number)
          const commentUserId = String(comment.user_id);
          const currentUserId = current_user?.user_id ? String(current_user.user_id) : '';

          console.log(`Comment ID: ${comment.id}, Comment User ID: ${commentUserId}, Current User ID: ${currentUserId}`);

          // Check if this comment was created by the current user
          const isCurrentUser = commentUserId === currentUserId;
          console.log(`Is current user's comment: ${isCurrentUser}`);

          // Try to find the user in group members or use default fallback values
          const memberMatch = expenseData.splits.find((split: any) =>
            split.member && String(split.member.user_id) === commentUserId
          )?.member;

          return {
            ...comment,
            // Use 'You' for current user's comments, otherwise use member name or fallback
            user_name: isCurrentUser ? 'You' : (memberMatch ? memberMatch.display_name || memberMatch.email : 'User'),
            user_avatar: memberMatch?.avatar_url || null,
            is_owner: isCurrentUser // Set ownership flag based on user ID match
          };
        });
        setComments(processedComments);
      } catch (err) {
        console.error('Error fetching expense details:', err);
        setError('Failed to load expense details');
      } finally {
        setLoading(false);
      }
    };

    fetchExpenseDetails();
  }, [isOpen, expenseId, groupId, accessToken, current_user?.user_id]);

  // Update new comment data when adding a comment
  const processNewComment = (comment: any) => {
    console.log("Processing new comment:", comment);
    console.log("Current user when adding comment:", current_user);

    // Always set user_name to 'You' for new comments created by the current user
    const newComment = {
      ...comment,
      user_name: 'You',
      user_avatar: current_user?.picture_url || null,
      is_owner: true // Current user is always the owner of a new comment
    };

    console.log("Processed new comment:", newComment);
    return newComment;
  };

  const handleSubmitComment = async (values: { text: string }) => {
    if (!expense) return;

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}/comments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text: values.text }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add comment');
      }

      const newComment = await response.json();
      const processedComment = processNewComment(newComment);
      setComments(prev => [...prev, processedComment]);
      commentForm.reset();

      notifications.show({
        title: 'Success',
        message: 'Comment added successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to add comment',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      const response = await fetch(
        `${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }

      setComments(prev => prev.filter(c => c.id !== commentId));
      notifications.show({
        title: 'Success',
        message: 'Comment deleted successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete comment',
        color: 'red',
      });
    }
  };

  const handleDeleteExpense = async () => {
    if (!expense) return;

    if (!confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onDelete(expenseId);

      // Close the modal first to provide instant feedback
      onClose();

      // Display success notification
      notifications.show({
        title: 'Success',
        message: 'Expense deleted successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete expense',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDeleteImage = async () => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}/expense-image`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      setExpense((prev: Expense | null) => prev ? { ...prev, expense_image_url: undefined } : null);

      notifications.show({
        title: 'Success',
        message: 'Image deleted successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete image',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${env.API_URL}/api/groups/${groupId}/expenses/${expenseId}/expense-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();

      setExpense((prev: Expense | null) => prev ? {
        ...prev,
        expense_image_url: data.expense_image_url,
        thumbnail_url: data.thumbnail_url
      } : null);

      notifications.show({
        title: 'Success',
        message: 'Image uploaded successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to upload image',
        color: 'red',
      });
    } finally {
      setIsUploadingImage(false);
    }
  }, [groupId, expenseId, accessToken]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.heic'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024,
    maxFiles: 1,
    multiple: false,
  });

  if (loading || !expense) {
    return (
      <Modal
        opened={isOpen}
        onClose={onClose}
        title="Expense Details"
        size="lg"
        centered
      >
        <LoadingOverlay visible={true} />
        <div style={{ height: 400 }}></div>
      </Modal>
    );
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<Text size="lg" fw={600}>{expense.description}</Text>}
      size="lg"
      centered
    >
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isSubmitting} />

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="details">
              Details
            </Tabs.Tab>
            <Tabs.Tab value="comments">
              Comments
              {comments.length > 0 && (
                <Badge ml={4} size="xs">{comments.length}</Badge>
              )}
            </Tabs.Tab>
          </Tabs.List>

          <div style={{ minHeight: '50vh', position: 'relative', marginTop: 20 }}>
            <Tabs.Panel value="details">
              <Stack>
                {/* Expense Summary */}
                <Paper withBorder p="md">
                  <Group justify="apart">
                    <div>
                      <Text size="xl" fw={700} color="green">
                        {formatCurrency(expense.amount, expense.currency_code)}
                      </Text>

                      <Group gap="xs" mt={4}>
                        <img src={clockIcon} alt="Date" width={14} />
                        <Text size="sm">
                          {formatDate(expense.date)}
                        </Text>

                        {expense.is_recurring && (
                          <Badge color="blue">Recurring</Badge>
                        )}

                        {expense.category && (
                          <Badge
                            color="gray"
                            leftSection={<img src={categoryIcon} alt="Category" width={10} />}
                          >
                            {expense.category.name}
                          </Badge>
                        )}
                      </Group>
                    </div>

                    <Group>
                      <ActionIcon
                        variant="light"
                        onClick={() => onEdit(expense)}
                      >
                        <img src={editIcon} alt="Edit" width={16} />
                      </ActionIcon>

                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={handleDeleteExpense}
                      >
                        <img src={trashIcon} alt="Delete" width={16} />
                      </ActionIcon>
                    </Group>
                  </Group>

                  <Divider my="md" />

                  <Group>
                    <Avatar
                      src={expense.paid_by.avatar_url}
                      radius="xl"
                      size="md"
                    >
                      {expense.paid_by.display_name?.charAt(0) || expense.paid_by.email.charAt(0)}
                    </Avatar>
                    <div>
                      <Text size="sm">Paid by</Text>
                      <Text fw={500}>{expense.paid_by.display_name || expense.paid_by.email}</Text>
                    </div>
                  </Group>

                  {expense.notes && (
                    <>
                      <Divider my="md" />
                      <Text ta="center" c="dimmed">{expense.notes}</Text>
                    </>
                  )}

                  {/* Add image display here if exists */}
                  {expense.expense_image_url ? (
                    <>
                      <Divider my="md" label="Expense Image" labelPosition="center" />
                      <Paper withBorder p="xs">
                        <div style={{ position: 'relative' }}>
                          <LoadingOverlay visible={isUploadingImage} />
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={() => window.open(expense.expense_image_url?.startsWith('http')
                              ? expense.expense_image_url
                              : `${env.R2_PUBLIC_URL}/${expense.expense_image_url}`, '_blank')}
                          >
                            <AsyncImage
                              src={(expense.thumbnail_url || expense.expense_image_url)?.startsWith('http')
                                ? (expense.thumbnail_url || expense.expense_image_url)
                                : `${env.R2_PUBLIC_URL}/${expense.thumbnail_url || expense.expense_image_url}`}
                              alt="Expense Image"
                              fit="contain"
                              height={300}
                              fallbackSrc="/placeholder.png"
                              loadingHeight={300}
                            />
                          </div>
                          <Group justify="flex-end" mt="xs">
                            <Button
                              variant="subtle"
                              size="sm"
                              onClick={() => window.open(expense.expense_image_url?.startsWith('http')
                                ? expense.expense_image_url
                                : `${env.R2_PUBLIC_URL}/${expense.expense_image_url}`, '_blank')}
                            >
                              View Full Image
                            </Button>
                            <Button
                              variant="subtle"
                              color="red"
                              size="sm"
                              onClick={handleDeleteImage}
                              loading={isSubmitting}
                            >
                              Delete Image
                            </Button>
                          </Group>
                        </div>
                      </Paper>
                    </>
                  ) : (
                    <>
                      <Divider my="md" label="Expense Image" labelPosition="center" />
                      <Paper withBorder p="xs">
                        <div {...getRootProps()} style={{ cursor: 'pointer' }}>
                          <input {...getInputProps()} />
                          <Group justify="center" gap="xs" py="xl">
                            <img src={uploadIcon} alt="Upload" width={24} />
                            <Text>Drop an image or PDF here or click to upload</Text>
                          </Group>
                          <Text size="xs" c="dimmed" ta="center" mt={5}>
                            Supported formats: JPG, PNG, HEIC, PDF - Max 10MB
                          </Text>
                        </div>
                      </Paper>
                    </>
                  )}
                </Paper>

                {/* Split Details */}
                <Paper withBorder p="md">
                  <Text fw={500} mb="md">
                    Split Details ({expense.split_type})
                  </Text>

                  {expense.splits.map((split: ExpenseSplit) => (
                    <Group key={split.id} justify="apart" mb="xs">
                      <Group>
                        <Avatar
                          src={split.member.avatar_url}
                          radius="xl"
                          size="sm"
                        >
                          {split.member.display_name ? split.member.display_name.charAt(0) : split.member.email.charAt(0)}
                        </Avatar>

                        <Text>{split.member.display_name || split.member.email}</Text>
                      </Group>

                      <Group gap="xs">
                        <Text fw={500}>
                          {formatCurrency(split.amount, expense.currency_code)}
                        </Text>

                        {expense.split_type === 'percentage' && (
                          <Text size="xs" c="dimmed">
                            ({split.percentage}%)
                          </Text>
                        )}

                        {expense.split_type === 'shares' && (
                          <Text size="xs" c="dimmed">
                            ({split.shares} shares)
                          </Text>
                        )}
                      </Group>
                    </Group>
                  ))}
                </Paper>

                {/* Expense Metadata */}
                <Paper withBorder p="md">
                  <Grid>
                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Created by</Text>
                      <Text size="sm">{expense.created_by_name || 'Unknown'}</Text>
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <Text size="xs" c="dimmed">Created on</Text>
                      <Text size="sm">{formatDate(expense.created_at)}</Text>
                    </Grid.Col>

                    {expense.updated_at && expense.updated_at !== expense.created_at && (
                      <>
                        <Grid.Col span={6}>
                          <Text size="xs" c="dimmed">Last updated</Text>
                          <Text size="sm">{formatDate(expense.updated_at)}</Text>
                        </Grid.Col>
                      </>
                    )}
                  </Grid>
                </Paper>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="comments">
              <Stack>
                {comments.length === 0 ? (
                  <Paper withBorder p="md">
                    <Text ta="center" c="dimmed">
                      No comments yet. Be the first to comment!
                    </Text>
                  </Paper>
                ) : (
                  <Paper withBorder p="md">
                    <Stack>
                      {comments.map((comment: any) => (
                        <Paper key={comment.id} p="sm" withBorder>
                          <Group justify="apart" mb={4}>
                            <Group>
                              <Avatar
                                src={comment.user_avatar}
                                radius="xl"
                                size="sm"
                              >
                                {comment.user_name?.charAt(0) || '?'}
                              </Avatar>

                              <div>
                                <Text size="sm" fw={500}>{comment.user_name || 'Unknown User'}</Text>
                                <Text size="xs" c="dimmed">
                                  {formatDate(comment.created_at)}
                                </Text>
                              </div>
                            </Group>

                            {comment.is_owner && (
                              <Menu position="bottom-end">
                                <Menu.Target>
                                  <ActionIcon variant="subtle" size="xs">
                                    <img src={dotsIcon} alt="Options" width={14} />
                                  </ActionIcon>
                                </Menu.Target>

                                <Menu.Dropdown>
                                  <Menu.Item
                                    color="red"
                                    onClick={() => {
                                      console.log("Deleting comment ID:", comment.id);
                                      handleDeleteComment(comment.id);
                                    }}
                                  >
                                    Delete
                                  </Menu.Item>
                                </Menu.Dropdown>
                              </Menu>
                            )}
                          </Group>

                          <Text>{comment.text}</Text>
                        </Paper>
                      ))}
                    </Stack>
                  </Paper>
                )}

                <Paper withBorder p="md">
                  <form onSubmit={commentForm.onSubmit(handleSubmitComment)}>
                    <TextInput
                      placeholder="Add a comment..."
                      rightSection={
                        <ActionIcon
                          type="submit"
                          disabled={!commentForm.values.text.trim()}
                        >
                          <img src={sendIcon} alt="Send" width={16} />
                        </ActionIcon>
                      }
                      {...commentForm.getInputProps('text')}
                    />
                  </form>
                </Paper>
              </Stack>
            </Tabs.Panel>
          </div>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="subtle" onClick={onClose}>
            Close
          </Button>

          <Button onClick={() => {
            // Make sure all required fields are included before passing to edit
            const editExpense = {
              ...expense,
              // Make sure these fields exist for the form
              description: expense.title,
              notes: expense.notes || '',
              recurring: expense.is_recurring,
              recurring_frequency: expense.recurrence_pattern,
              recurring_end_date: expense.recurrence_end_date,
              paid_by: {
                ...expense.paid_by,
                id: String(expense.paid_by.id), // Ensure paid_by.id is a string
              },
            };
            onEdit(editExpense);
          }}>
            Edit Expense
          </Button>
        </Group>
      </div>
    </Modal>
  );
};

export default ExpenseDetail; 